import { forwardRef, useEffect } from "react";

import { globalProps } from "neetocommons/initializers";
import PropTypes from "prop-types";

import ProfileButton from "./components/ProfileButton";
import "./floating-action-menu.scss";
import { setAppTheme } from "./utils";

const FloatingActionMenu = forwardRef(
  (
    {
      profileInfoOverrides = {},
      topLinks = [],
      bottomLinks = [],
      showProductSwitcher = true,
      isThemeSwitcherEnabled = false,
      isOrganizationSwitcherEnabled = false,
      customContent,
      profilePopoverProps = {},
      isConsumer = false,
      enableSubscriptionUpgradeRequest = true,
      ...otherProps
    },
    ref
  ) => {
    useEffect(() => {
      isThemeSwitcherEnabled && setAppTheme();
    }, []);

    const isBigBinaryEmail =
      globalProps?.user?.email?.endsWith("@bigbinary.com");

    return (
      <div
        {...{ ref }}
        className="neeto-molecules-floating-action-menu__container"
        data-testid="floating-action-menu-container"
        {...otherProps}
      >
        <ProfileButton
          {...{
            bottomLinks,
            customContent,
            enableSubscriptionUpgradeRequest,
            isConsumer,
            isOrganizationSwitcherEnabled,
            isThemeSwitcherEnabled,
            profileInfoOverrides,
            profilePopoverProps,
            showProductSwitcher,
            topLinks,
          }}
          isOrganizationSwitcherEnabled={
            isBigBinaryEmail || isOrganizationSwitcherEnabled
          }
        />
      </div>
    );
  }
);

FloatingActionMenu.displayName = "FloatingActionMenu";

FloatingActionMenu.propTypes = {
  /**
   * To specify the profile info to be rendered in the menu
   */
  profileInfoOverrides: PropTypes.shape({
    name: PropTypes.string,
    email: PropTypes.string,
    imageUrl: PropTypes.string,
  }),
  /**
   * Custom content to be rendered in the menu between the top and bottom links if any
   */
  customContent: PropTypes.element,
  /**
   * Any extra links to be rendered at the top of the menu.
   */
  topLinks: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      label: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    })
  ),
  /**
   * Any extra links to be rendered at the bottom of the menu.
   */
  bottomLinks: PropTypes.arrayOf(
    PropTypes.shape({
      onClick: PropTypes.func,
      label: PropTypes.string,
      icon: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
    })
  ),
  /**
   * To display the consumer profile info in the menu.
   */
  isConsumer: PropTypes.bool,
  /**
   * To display the product switcher menu option.
   */
  showProductSwitcher: PropTypes.bool,
  /**
   * To display the app theme switcher above the user profile.
    If this prop is set to `true`, it will set the last selected
    theme to the app when the component is mounted.
   */
  isThemeSwitcherEnabled: PropTypes.bool,
  /**
   * To display the organization switcher above the user profile.
    If this prop is set to `true`, it lists all the logged in
    organizations for the current user.
   */
  isOrganizationSwitcherEnabled: PropTypes.bool,
  /**
   * When true, free-plan users see a modal to request a Pro upgrade
   * instead of being redirected to the billing page.
   */
  enableSubscriptionUpgradeRequest: PropTypes.bool,
  /**
   * To show help icon popover for preferred date format, preferred time format and time zone.
   */
  profilePopoverProps: PropTypes.shape({
    preferredDateFormat: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      helpLinkProps: PropTypes.shape({
        label: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    preferredTimeFormat: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      helpLinkProps: PropTypes.shape({
        label: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
    timeZone: PropTypes.shape({
      title: PropTypes.string,
      description: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
      helpLinkProps: PropTypes.shape({
        label: PropTypes.string,
        href: PropTypes.string,
      }),
    }),
  }),
};

export default FloatingActionMenu;
